import React, { useEffect, useState } from "react";
import { Plus, Trash2, Edit2, Image as ImgIcon, X } from "lucide-react";
import api from "../../lib/api";

const fileToBase64 = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
});

const EMPTY = {
    title: "", content: "", image_base64: "", image_focal_y: 50,
    facebook_url: "", instagram_url: "", published: true,
};

export default function AdminNews() {
    const [list, setList] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);

    const load = () => api.get("/news?only_published=false").then(r => setList(r.data));
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        const payload = { ...form, image_focal_y: Number(form.image_focal_y) || 50 };
        if (editing) await api.put(`/news/${editing}`, payload);
        else await api.post("/news", payload);
        setEditing(null);
        setForm(EMPTY);
        load();
    };

    const del = async (id) => {
        if (!window.confirm("Supprimer ?")) return;
        await api.delete(`/news/${id}`);
        load();
    };

    const onFile = async (e) => {
        const f = e.target.files[0];
        if (!f) return;
        setForm({ ...form, image_base64: await fileToBase64(f) });
    };

    const removeImage = () => setForm({ ...form, image_base64: "", image_focal_y: 50 });

    const focalY = Number(form.image_focal_y ?? 50);

    return (
        <div data-testid="admin-news">
            <h1 className="font-display text-4xl uppercase tracking-widest mb-6">Nouvelles</h1>
            <form onSubmit={save} className="glass-card p-5 grid gap-3 mb-6">
                <input required className="input-field" placeholder="Titre" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="news-title" />
                <textarea required rows={6} className="input-field" placeholder="Contenu" value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} data-testid="news-content" />
                <div className="grid sm:grid-cols-2 gap-3">
                    <input className="input-field" placeholder="Lien Facebook (optionnel)" value={form.facebook_url} onChange={e => setForm({ ...form, facebook_url: e.target.value })} />
                    <input className="input-field" placeholder="Lien Instagram (optionnel)" value={form.instagram_url} onChange={e => setForm({ ...form, instagram_url: e.target.value })} />
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <label className="btn-ghost cursor-pointer flex items-center gap-2" data-testid="news-upload-btn">
                        <ImgIcon size={14} />
                        {form.image_base64 ? "Changer l'image" : "Ajouter une image"}
                        <input type="file" accept="image/*" onChange={onFile} className="hidden" />
                    </label>
                    {form.image_base64 && (
                        <button type="button" onClick={removeImage} className="btn-ghost text-rose-400 border-rose-400/30 flex items-center gap-2" data-testid="news-remove-img">
                            <X size={12} /> Retirer
                        </button>
                    )}
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published} onChange={e => setForm({ ...form, published: e.target.checked })} /> Publié</label>
                    <button type="submit" className="btn-primary ml-auto"><Plus size={14} /> {editing ? "Modifier" : "Publier"}</button>
                </div>

                {/* Cover image editor: preview + vertical recentering slider */}
                {form.image_base64 && (
                    <div className="mt-2 border-t border-white/10 pt-4">
                        <div className="label-mini mb-2">Aperçu photo de couverture (recentrer verticalement)</div>
                        <div className="w-full h-48 sm:h-56 overflow-hidden bg-black/40 mb-3">
                            <img
                                src={form.image_base64}
                                alt="Aperçu"
                                className="w-full h-full object-cover"
                                style={{ objectPosition: `50% ${focalY}%` }}
                                data-testid="news-preview-img"
                            />
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-xs text-white/50 shrink-0 w-8 text-right">Haut</span>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                step="1"
                                value={focalY}
                                onChange={e => setForm({ ...form, image_focal_y: parseInt(e.target.value, 10) })}
                                className="flex-1 accent-[#7FD93C]"
                                data-testid="news-focal-y"
                            />
                            <span className="text-xs text-white/50 shrink-0 w-8">Bas</span>
                            <span className="font-mono text-xs text-[#7FD93C] shrink-0 w-10 text-right">{focalY}%</span>
                            <button
                                type="button"
                                onClick={() => setForm({ ...form, image_focal_y: 50 })}
                                className="btn-ghost text-xs shrink-0"
                                data-testid="news-focal-reset"
                            >
                                Réinit.
                            </button>
                        </div>
                        <div className="text-[11px] text-white/40 mt-2">
                            Ajustez le curseur pour recentrer la partie importante (visages, action) qui doit rester visible sur les aperçus et l&apos;accueil.
                        </div>
                    </div>
                )}
            </form>

            <div className="grid gap-3">
                {list.map(n => (
                    <div key={n.id} className="glass-card p-4 flex items-center gap-3">
                        {n.image_base64
                            ? <img
                                src={n.image_base64}
                                alt=""
                                className="h-14 w-20 sm:h-16 sm:w-24 object-cover shrink-0"
                                style={{ objectPosition: `50% ${n.image_focal_y ?? 50}%` }}
                            />
                            : <div className="h-14 w-20 sm:h-16 sm:w-24 bg-black/40 diag-stripes shrink-0" />
                        }
                        <div className="flex-1 min-w-0">
                            <div className="font-display text-base sm:text-xl uppercase truncate">{n.title}</div>
                            <div className="text-xs text-white/50">{new Date(n.created_at).toLocaleDateString("fr-CA")} • {n.published ? "Publié" : "Brouillon"}</div>
                        </div>
                        <div className="flex gap-1 shrink-0">
                            <button
                                onClick={() => {
                                    setEditing(n.id);
                                    setForm({
                                        title: n.title,
                                        content: n.content,
                                        image_base64: n.image_base64 || "",
                                        image_focal_y: n.image_focal_y ?? 50,
                                        facebook_url: n.facebook_url || "",
                                        instagram_url: n.instagram_url || "",
                                        published: n.published,
                                    });
                                    window.scrollTo({ top: 0, behavior: "smooth" });
                                }}
                                className="btn-ghost p-2"
                                data-testid={`news-edit-${n.id}`}
                            ><Edit2 size={12} /></button>
                            <button onClick={() => del(n.id)} className="btn-ghost p-2 text-rose-400 border-rose-400/30"><Trash2 size={12} /></button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
